import { useEffect, useMemo, useState } from 'react';

// mui imports
import { TextField, Typography, Box, IconButton, Stack } from '@mui/material';

// third party imports
import { useIntl } from 'react-intl';

// project imports
import { useDispatch, useSelector } from 'store';
import AutocompleteVirtualized from './AutocompleteVirtualized';
import { Replay } from '@mui/icons-material';

import { OptionType } from './commons';
import { getVendors } from 'store/slices/catalog';
import { VendorType } from 'types/components/shared';

export type VendorSelectorProps = {
    onChange?: (vendor: VendorType | null) => void;
    required?: boolean;
    selectedVendorId?: number | string | null;
    showReload?: boolean;
    readOnly?: boolean;
    label?: string;
};

export default function VendorSelector({
    onChange,
    required = false,
    selectedVendorId,
    showReload = false,
    readOnly,
    label
}: VendorSelectorProps) {
    const intl = useIntl();
    const dispatch = useDispatch();

    const { vendors } = useSelector((state) => state.catalogue);
    const { fatherMerchant } = useSelector((state) => state.auth);

    const [inputValue, setInputValue] = useState('');
    const [localLoading, setLocalLoading] = useState(true);

    // Normalizar id
    const normalizedVendorId = useMemo(() => (isNaN(Number(selectedVendorId)) ? undefined : Number(selectedVendorId)), [selectedVendorId]);

    const options: OptionType[] = useMemo(() => {
        setLocalLoading(true);

        let list = vendors;

        if (!list?.length) return [];

        const mapped = list.map((vendor: VendorType) => ({
            id: Number(vendor.idProvider),
            label: vendor.name
        }));

        setLocalLoading(false);
        return mapped;
    }, [vendors]);

    const selectedValue: OptionType | null = useMemo(() => {
        if (!normalizedVendorId) return null;

        const found = options.find((opt) => opt.id === normalizedVendorId);
        if (!found) return null;

        return { id: found.id, label: found.label };
    }, [normalizedVendorId, options]);

    useEffect(() => {
        if (!vendors.length) {
            dispatch(getVendors({ idMerchant: fatherMerchant?.merchantId })).finally(() => setLocalLoading(false));
        }
    }, [dispatch, fatherMerchant?.merchantId, vendors]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: OptionType | null) => {
        if (onChange) {
            const vendorItem = vendors.find((item) => {
                return item.idProvider === newValue?.id;
            });

            onChange(vendorItem ?? null);
        }
    };

    const reloadVendors = () => {
        setLocalLoading(true);
        dispatch(getVendors({ idMerchant: fatherMerchant?.merchantId })).finally(() => setLocalLoading(false));
    };

    return (
        <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
            <AutocompleteVirtualized
                fullWidth
                readOnly={readOnly}
                value={selectedValue}
                onChange={handleChange}
                options={options}
                loading={localLoading}
                inputValue={inputValue}
                onInputChange={(event, newValue) => setInputValue(newValue)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={
                            label ??
                            intl.formatMessage({
                                id: 'vendor'
                            })
                        }
                        required={required}
                    />
                )}
                isOptionEqualToValue={(opt, value) => opt.id === value.id}
                getOptionLabel={(opt) => `${opt.id} - ${opt.label}`}
                noOptionsText={
                    <Box sx={{ p: 1 }}>
                        <Typography>
                            {intl.formatMessage({
                                id: 'no_matches_found'
                            })}
                        </Typography>
                    </Box>
                }
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={`vendor-${option.id}`} sx={{ display: 'flex' }}>
                        <Typography
                            sx={{
                                whiteSpace: 'nowrap',
                                fontSize: 12
                            }}
                        >
                            <Typography component="span" variant="caption" sx={{ mr: 0.3, fontSize: 12 }}>
                                {`${option.id} - `}
                            </Typography>
                            {option.label}
                        </Typography>
                    </Box>
                )}
            />

            {showReload && (
                <IconButton size="small" sx={{ ml: 1 }} onClick={reloadVendors} title={intl.formatMessage({ id: 'reload' })}>
                    <Replay sx={{ fontSize: 17 }} />
                </IconButton>
            )}
        </Stack>
    );
}
